import DeviceSpecificationController from "@/services/deviceSpecificationService";
import SpecificationController from "@/services/specificationService";
import DeviceTypeController from "@/services/deviceTypeService";
import Swal from "sweetalert2";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

export default {
  name: "DeviceSpecificationView",

  components: { Multiselect },

  data() {
    return {
      items: [],
      total: 0,
      loading: false,

      page: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      totalPages: 1,

      filters: {
        search: "",
        status: "",
        device_type_id: "",
      },

      specificationList: [],
      deviceTypeList: [],

      showModal: false,
      isEdit: false,
      saving: false,
      formError: "",

      form: {
        id: null,
        specification_id: [], // array — bisa pilih banyak spesifikasi saat tambah
        device_type_id: null,
        status: 1,
      },

      errors: {
        specification_id: "",
        device_type_id: "",
      },

      toast: {
        show: false,
        message: "",
        type: "success",
      },
      toastTimer: null,
    };
  },

  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.page;
      const delta = 2;
      const range = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) range.unshift("...");
      if (current + delta < total - 1) range.push("...");

      range.unshift(1);
      if (total > 1) range.push(total);

      return range;
    },
  },

  mounted() {
    this.loadDropdowns();
    this.getData();
  },

  methods: {
    async loadDropdowns() {
      try {
        const [specRes, deviceTypeRes] = await Promise.all([
          SpecificationController.getAll({}),
          DeviceTypeController.getAll({}),
        ]);
        this.specificationList = specRes?.data ?? [];
        this.deviceTypeList = deviceTypeRes?.data ?? [];
      } catch (error) {
        console.error(error);
      }
    },

    async getData() {
      this.loading = true;

      const result = await DeviceSpecificationController.getAll({});
      let filtered = result?.data ?? [];

      if (this.filters.search) {
        const q = this.filters.search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.specification_name?.toLowerCase().includes(q) ||
            item.device_type_name?.toLowerCase().includes(q),
        );
      }

      if (this.filters.status !== "") {
        const s = parseInt(this.filters.status);
        filtered = filtered.filter((item) =>
          item.status !== undefined ? item.status == s : true,
        );
      }

      if (this.filters.device_type_id !== "") {
        const dt = parseInt(this.filters.device_type_id);
        filtered = filtered.filter((item) => item.device_type_id == dt);
      }

      this.total = filtered.length;
      this.totalPages = Math.ceil(this.total / this.perPage) || 1;

      // kalau lagi di halaman terakhir terus perPage diperbesar / hasil filter mengecil,
      // pastikan page gak nyangkut di luar jangkauan
      if (this.page > this.totalPages) this.page = this.totalPages;

      const start = (this.page - 1) * this.perPage;
      this.items = filtered.slice(start, start + this.perPage);

      this.loading = false;
    },

    changePerPage() {
      this.page = 1;
      this.getData();
    },

    changePage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.getData();
    },

    openModal() {
      this.isEdit = false;
      this.formError = "";
      this.clearErrors();
      this.form = {
        id: null,
        specification_id: [],
        device_type_id: null,
        status: 1,
      };
      this.showModal = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.formError = "";
      this.clearErrors();
      this.form = {
        id: item.id,
        specification_id: [item.specification_id],
        device_type_id: item.device_type_id,
        status: item.status ?? 1,
      };
      this.showModal = true;
    },

    closeModal() {
      if (this.saving) return;
      this.showModal = false;
      this.formError = "";
      this.clearErrors();
    },

    validateForm() {
      this.clearErrors();
      let valid = true;

      if (!this.form.device_type_id) {
        this.errors.device_type_id = "Jenis perangkat wajib dipilih.";
        valid = false;
      }

      if (
        !this.form.specification_id ||
        this.form.specification_id.length === 0
      ) {
        this.errors.specification_id = "Minimal 1 spesifikasi wajib dipilih.";
        valid = false;
      }

      return valid;
    },

    clearErrors() {
      this.errors = {
        specification_id: "",
        device_type_id: "",
      };
    },

    async saveForm() {
      if (!this.validateForm()) return;

      this.saving = true;
      this.formError = "";

      try {
        if (this.isEdit) {
          const payload = {
            specification_id: this.form.specification_id[0],
            device_type_id: this.form.device_type_id,
            status: this.form.status,
          };
          const result = await DeviceSpecificationController.update(
            this.form.id,
            payload,
          );

          if (!result.success) {
            this.formError = result.message || "Gagal menyimpan data.";
            return;
          }
        } else {
          const failedItems = [];

          for (const specId of this.form.specification_id) {
            const payload = {
              specification_id: specId,
              device_type_id: this.form.device_type_id,
              status: this.form.status,
            };
            const result = await DeviceSpecificationController.create(payload);

            if (!result.success) {
              const specName =
                this.specificationList.find(
                  (s) => s.specification_id === specId,
                )?.specification_name || specId;
              failedItems.push(`${specName}: ${result.message}`);
            }
          }

          if (failedItems.length > 0) {
            this.formError = failedItems.join(" | ");
            // tetap refresh data karena sebagian mungkin berhasil
            this.getData();
            return;
          }
        }
        this.saving = false;
        this.closeModal();
        this.page = 1;
        this.getData();
        this.showToast(
          this.isEdit
            ? "Spesifikasi perangkat berhasil diperbarui."
            : "Spesifikasi perangkat berhasil ditambahkan.",
          "success",
        );
      } catch (error) {
        console.error(error);
        this.formError = "Terjadi kesalahan saat menyimpan data.";
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(item) {
      const newStatus = item.status == 1 ? 0 : 1;
      const action = newStatus === 1 ? "mengaktifkan" : "menonaktifkan";
      const label = newStatus === 1 ? "diaktifkan" : "dinonaktifkan";

      const confirm = await Swal.fire({
        title: "Konfirmasi",
        text: `Yakin ingin ${action} spesifikasi perangkat ini?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Ya",
        cancelButtonText: "Batal",
        confirmButtonColor: "#198754",
      });

      if (!confirm.isConfirmed) return;

      const result = await DeviceSpecificationController.updateStatus(
        item.id,
        newStatus,
      );

      if (result.success) {
        this.getData();
        this.showToast(`Spesifikasi perangkat berhasil ${label}.`, "success");
      } else {
        this.showToast(result.message || "Gagal mengubah status.", "danger");
      }
    },

    showToast(message, type = "success") {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { show: true, message, type };
      this.toastTimer = setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },
  },

  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
};
